/**
 * Pure row -> domain mappers for SpacetimeDB `useTable` results.
 *
 * Row typing decision (see Part B Task 2 report for full detail): row shapes
 * are declared STRUCTURALLY here rather than imported from the generated
 * `src/module_bindings/*_table.ts` row builders or their `Infer<>` aliases in
 * `src/module_bindings/types.ts`. Two independent reasons:
 *
 * 1. Every generated `*_table.ts` file does `import { t as __t, ... } from
 *    "spacetimedb"` as a VALUE (needed to build its schema at module load).
 *    That package fails to resolve under jest-environment-jsdom's "browser"
 *    export condition (confirmed in Part A / Part B Task 1's
 *    use-table-proof.test.tsx). A `import type { X } from
 *    "@/module_bindings/types"` is fully erased at compile time (verified
 *    with a throwaway probe test before writing this file) and would in fact
 *    be safe from that specific problem.
 * 2. BUT those generated types type timestamp columns as the SDK's
 *    `Timestamp` class, which has a public `__timestamp_micros_since_unix_epoch__:
 *    bigint` field plus several methods. Building test fixtures that satisfy
 *    that full shape (without importing the real class, which reintroduces
 *    problem #1) is impractical. So every row interface below re-declares its
 *    timestamp columns as the minimal `StdbTimestamp` structural interface
 *    instead, keeping test fixtures to a one-line `{ toDate: () => Date }`.
 *
 * Every field name/optionality below was verified by direct reading of the
 * corresponding generated file (not guessed): my_user_table.ts,
 * my_organizations_table.ts, org_members_table.ts, org_users_table.ts,
 * org_api_keys_table.ts, org_tax_exemptions_table.ts, org_subscriptions_table.ts,
 * org_playbooks_table.ts, org_glaci_tags_table.ts, org_playbook_tags_table.ts.
 * If the module's schema changes, these must be kept in sync by hand.
 */

import type {
  Organization,
  GlaciCredentialStatus,
  GlaciOrganizationFeatures,
  GlaciCoreInstance,
  GlaciOrganizationBranding,
} from "@/types/organization";
import type { User } from "@/types/user";
import type { Member } from "@/types/member";
import type { ApiKey } from "@/types/api-keys";
import type { TaxExemption } from "@/types/tax-exemption";
import type {
  Subscription,
  SubscriptionCollectionMethod,
} from "@/types/subscription";
import type {
  GlaciPlaybook,
  GlaciTag,
  Author,
  PlaybookExecutionType,
} from "@/types/glaci";
import type { OrganizationContextCatalog } from "@libum-llc/glaci-types";

/** Minimal structural stand-in for the SDK's `Timestamp` class. */
export interface StdbTimestamp {
  toDate(): Date;
}

/** `my_user` row (verified against `my_user_table.ts`). */
export interface MyUserRow {
  id: string;
  email: string;
  name: string;
  avatar: string;
  lastSelectedOrganizationId?: string;
  lastSignInAt?: StdbTimestamp;
  createdAt?: StdbTimestamp;
  updatedAt?: StdbTimestamp;
}

/** `my_organizations` row (verified against `my_organizations_table.ts`). */
export interface MyOrganizationRow {
  id: string;
  name: string;
  ownerId: string;
  customerId: string;
  logo: string;
  isGlaciEnabled?: boolean;
  isInGracePeriod?: boolean;
  hasRequestedGlaciAccess?: boolean;
  runtimeUri?: string;
  runtimeModule?: string;
  glaciContextCatalog?: string;
  glaciFeatures?: string;
  glaciInstances?: string;
  glaciBranding?: string;
  glaciCredentialMetadata?: string;
  createdAt?: StdbTimestamp;
  updatedAt?: StdbTimestamp;
}

/** `org_members` row (verified against `org_members_table.ts`). */
export interface OrgMemberRow {
  id: string;
  userId: string;
  organizationId: string;
  inviterId: string;
  isPowerOnProPlus: boolean;
  role: string;
  status: string;
  createdAt?: StdbTimestamp;
  updatedAt?: StdbTimestamp;
}

/**
 * `my_memberships` row — the caller's OWN member rows, still a full
 * passthrough of the members table (invite_token included; it is only ever
 * the caller's own token, which is not an exposure). `org_members` is the
 * projected co-member view without the token.
 */
export type MyMembershipRow = OrgMemberRow & { inviteToken: string };

/**
 * `org_users` row (verified against `org_users_table.ts`) — masked join
 * projection. `lastSignInAt` was added for legacy `/api/member` join parity
 * (fix report b-task-2-fix); it is NOT new exposure — this view already
 * exposed the caller's co-members' name/email/avatar.
 */
export interface OrgUserRow {
  id: string;
  name: string;
  email: string;
  avatar: string;
  lastSignInAt?: StdbTimestamp;
}

/** `org_api_keys` row (verified against `org_api_keys_table.ts`) — masked 7-column projection. */
export interface OrgApiKeyRow {
  id: string;
  organizationId: string;
  userId: string;
  product: string;
  apiKey: string;
  isPowerOnProPlus?: boolean;
  createdAt?: StdbTimestamp;
}

/** `org_tax_exemptions` row (verified against `org_tax_exemptions_table.ts`). */
export interface OrgTaxExemptionRow {
  id: string;
  userId: string;
  organizationId: string;
  companyName: string;
  billingEmail: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  certificateKey: string;
  status: string;
  updatedAt?: StdbTimestamp;
}

/** `org_subscriptions` row (verified against `org_subscriptions_table.ts`). */
export interface OrgSubscriptionRow {
  id: string;
  organizationId: string;
  customerId: string;
  productId: string;
  priceId: string;
  status: string;
  quantity: number;
  cancelAt?: StdbTimestamp;
  canceledAt?: StdbTimestamp;
  cancelAtPeriodEnd: boolean;
  collectionMethod: string;
  lastEventAt: StdbTimestamp;
  createdAt?: StdbTimestamp;
  updatedAt?: StdbTimestamp;
}

/** `org_playbooks` row (verified against `org_playbooks_table.ts`). */
export interface OrgPlaybookRow {
  id: string;
  organizationId: string;
  userId: string;
  name: string;
  description?: string;
  icon?: string;
  category?: string;
  workflowName: string;
  nodes: string;
  connections: string;
  pinData?: string;
  author: string;
  executionType: string;
  version: string;
  isActive: boolean;
  isVisible: boolean;
  isOnHomeView: boolean;
  requiresAccount: boolean;
  createdAt?: StdbTimestamp;
  updatedAt?: StdbTimestamp;
}

/** `org_glaci_tags` row (verified against `org_glaci_tags_table.ts`). */
export interface OrgGlaciTagRow {
  id: string;
  organizationId: string;
  tagId: string;
  name: string;
  color?: string;
}

/** `org_playbook_tags` row (verified against `org_playbook_tags_table.ts`). */
export interface OrgPlaybookTagRow {
  id: string;
  organizationId: string;
  playbookId: string;
  tagId: string;
}

/** `ts ? ts.toDate().toISOString() : ""` — the one rule for every timestamp column. */
export function tsToIso(ts: StdbTimestamp | undefined): string {
  return ts ? ts.toDate().toISOString() : "";
}

/**
 * `ts ? Math.floor(ts.toDate().getTime() / 1000) : null` — the legacy
 * `Subscription` type carried Stripe's Unix-SECONDS timestamps for
 * `cancelAt`/`canceledAt`; consumers' date math (e.g. `formatLongDate`)
 * expects that unit, not milliseconds and not an ISO string.
 */
export function tsToUnixSecondsOrNull(
  ts: StdbTimestamp | undefined,
): number | null {
  return ts ? Math.floor(ts.toDate().getTime() / 1000) : null;
}

function parseJsonOrUndefined<T>(value: string | undefined): T | undefined {
  if (!value) return undefined;
  try {
    return JSON.parse(value) as T;
  } catch {
    return undefined;
  }
}

function parseJsonOr<T>(value: string | undefined, fallback: T): T {
  if (!value) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

const DEFAULT_AUTHOR: Author = { email: "", name: "" };

export function mapUser(row: MyUserRow): User {
  return {
    id: row.id,
    name: row.name,
    email: row.email,
    avatar: row.avatar,
    lastSignInAt: tsToIso(row.lastSignInAt),
    // Legacy-shape residue: Task 3's useCurrentUser fills this from
    // my_organizations ids so User consumers keep working.
    organizations: [],
  };
}

export function mapOrganization(row: MyOrganizationRow): Organization {
  return {
    id: row.id,
    name: row.name,
    ownerId: row.ownerId,
    customerId: row.customerId || "UNASSIGNED",
    logo: row.logo,
    isGlaciEnabled: row.isGlaciEnabled,
    isInGracePeriod: row.isInGracePeriod,
    hasRequestedGlaciAccess: row.hasRequestedGlaciAccess,
    glaciContextCatalog: parseJsonOrUndefined<OrganizationContextCatalog>(
      row.glaciContextCatalog,
    ),
    glaciFeatures: parseJsonOrUndefined<GlaciOrganizationFeatures>(
      row.glaciFeatures,
    ),
    glaciInstances: parseJsonOrUndefined<GlaciCoreInstance[]>(
      row.glaciInstances,
    ),
    glaciBranding: parseJsonOrUndefined<GlaciOrganizationBranding>(
      row.glaciBranding,
    ),
    glaciCredentialStatus: parseJsonOrUndefined<GlaciCredentialStatus>(
      row.glaciCredentialMetadata,
    ),
    glaciRuntimeTarget:
      row.runtimeUri && row.runtimeModule
        ? { uri: row.runtimeUri, module: row.runtimeModule }
        : undefined,
    createdAt: tsToIso(row.createdAt),
    updatedAt: tsToIso(row.updatedAt),
  };
}

export function mapMember(
  memberRow: OrgMemberRow,
  usersById: Map<string, OrgUserRow>,
): Member {
  const user = memberRow.userId.startsWith("user_")
    ? usersById.get(memberRow.userId)
    : undefined;

  return {
    id: memberRow.id,
    userId: memberRow.userId,
    organizationId: memberRow.organizationId,
    name: user ? user.name : "UNASSIGNED",
    email: user ? user.email : memberRow.userId,
    avatar: user ? user.avatar : "UNASSIGNED",
    inviterId: memberRow.inviterId,
    isPowerOnProPlus: memberRow.isPowerOnProPlus,
    // org_users now carries last_sign_in_at (legacy /api/member join parity —
    // see fix report b-task-2-fix); the invited/stale-user fallback branches
    // have no joined user row at all, so they keep the legacy-shape "" used
    // for every other UNASSIGNED field on those branches.
    lastSignInAt: user ? tsToIso(user.lastSignInAt) : "",
    role: memberRow.role as Member["role"],
    status: memberRow.status as Member["status"],
  };
}

export function mapApiKey(row: OrgApiKeyRow): ApiKey {
  return {
    id: row.id,
    organizationId: row.organizationId,
    userId: row.userId,
    product: row.product,
    // Already masked (sk_...<last4>) by the org_api_keys view — passed through as-is.
    apiKey: row.apiKey,
    // Retained on the domain type per the legacy response shape; SpacetimeDB
    // has no unit-tracking column feeding this.
    units: [],
    createdAt: tsToIso(row.createdAt),
  };
}

export function mapTaxExemption(row: OrgTaxExemptionRow): TaxExemption {
  return {
    id: row.id,
    userId: row.userId,
    organizationId: row.organizationId,
    companyName: row.companyName,
    billingEmail: row.billingEmail,
    addressLine1: row.addressLine1,
    addressLine2: row.addressLine2,
    city: row.city,
    state: row.state,
    postalCode: row.postalCode,
    certificateKey: row.certificateKey,
    status: row.status as TaxExemption["status"],
    updatedAt: tsToIso(row.updatedAt),
  };
}

export function mapSubscription(row: OrgSubscriptionRow): Subscription {
  return {
    id: row.id,
    seats: row.quantity,
    cancelAt: tsToUnixSecondsOrNull(row.cancelAt),
    cancelAtPeriodEnd: row.cancelAtPeriodEnd,
    canceledAt: tsToUnixSecondsOrNull(row.canceledAt),
    productId: row.productId,
    priceId: row.priceId,
    status: row.status,
    paymentMethod: row.collectionMethod as SubscriptionCollectionMethod,
  };
}

export function mapPlaybook(
  row: OrgPlaybookRow,
  tagIdsByPlaybook: Map<string, string[]>,
): GlaciPlaybook {
  return {
    playbookId: row.id,
    organizationId: row.organizationId,
    userId: row.userId,
    name: row.name,
    description: row.description,
    icon: row.icon,
    category: row.category,
    workflowName: row.workflowName,
    nodes: parseJsonOr(row.nodes, []),
    connections: parseJsonOr(row.connections, {}),
    pinData: parseJsonOrUndefined(row.pinData),
    author: parseJsonOr(row.author, DEFAULT_AUTHOR),
    executionType: row.executionType as PlaybookExecutionType,
    version: row.version,
    isActive: row.isActive,
    isVisible: row.isVisible,
    isOnHomeView: row.isOnHomeView,
    requiresAccount: row.requiresAccount,
    tagIds: tagIdsByPlaybook.get(row.id) ?? [],
    createdAt: tsToIso(row.createdAt),
    updatedAt: tsToIso(row.updatedAt),
  };
}

export function mapGlaciTag(row: OrgGlaciTagRow): GlaciTag {
  return {
    tagId: row.tagId,
    name: row.name,
    color: row.color,
  };
}

export function buildTagIdsByPlaybook(
  links: OrgPlaybookTagRow[],
): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const link of links) {
    const existing = map.get(link.playbookId);
    if (existing) {
      existing.push(link.tagId);
    } else {
      map.set(link.playbookId, [link.tagId]);
    }
  }
  return map;
}
